import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Link2, Check, Search, Send } from "lucide-react";
import { checkLibraryCourseUpdates, searchCoursesOnline, updateLibraryCourse, fetchLibraryCourseTrips, pushLibraryCourse } from "@/lib/api";

function PushToTrips({ item, onPushed }) {
  const [trips, setTrips] = useState(null);
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    fetchLibraryCourseTrips(item.id).then(setTrips).catch(() => setTrips([]));
  }, [item.id, item.updatedAt]);

  if (!trips || trips.length === 0) return null;
  const stale = trips.filter((t) => t.stale);

  const push = async () => {
    setPushing(true);
    try {
      const r = await pushLibraryCourse(item.id);
      toast.success(r.updatedTrips.length ? `Updated ${r.updatedCourses} course${r.updatedCourses === 1 ? "" : "s"} across ${r.updatedTrips.length} trip${r.updatedTrips.length === 1 ? "" : "s"}` : "All trips already up to date");
      onPushed?.(item);
      setTrips(trips.map((t) => ({ ...t, stale: false })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
    } finally { setPushing(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[rgba(27,51,34,0.15)]" data-testid={`push-trips-${item.id}`}>
      <div className="text-xs text-[color:var(--text-dark)]/80 mb-2">
        Used in {trips.length} trip{trips.length === 1 ? "" : "s"}: {trips.map((t) => t.name).join(", ")}
        {stale.length > 0 ? ` · ${stale.length} out of date` : " · all current"}
      </div>
      <button
        data-testid={`push-trips-btn-${item.id}`}
        disabled={pushing || stale.length === 0}
        className="brass-btn rounded-md px-4 py-1.5 text-sm inline-flex items-center gap-1 disabled:opacity-50"
        onClick={push}
      ><Send size={13} /> {pushing ? "Pushing…" : stale.length > 0 ? `Push update to ${stale.length} trip${stale.length === 1 ? "" : "s"}` : "Trips up to date"}</button>
    </div>
  );
}

/**
 * Inline panel shown under a library course when the user taps "Check for updates".
 * Handles: linking a manual course to GolfCourseAPI, showing the diff, applying it.
 */
export default function CourseUpdatePanel({ item, onApplied, onClose, onPushed }) {
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [query, setQuery] = useState(item.name);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);

  const check = async (sourceId) => {
    setChecking(true);
    try {
      const r = await checkLibraryCourseUpdates(item.id, sourceId);
      setResult(r);
      if (r.upToDate) toast.success(`"${item.name}" is up to date`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check for updates");
    } finally { setChecking(false); }
  };

  const search = async (q) => {
    if (!q || q.trim().length < 2) return;
    setSearching(true);
    try {
      const data = await searchCoursesOnline(q.trim());
      const list = Array.isArray(data) ? data : (data.courses || []);
      setMatches(list.slice(0, 8));
      if (list.length === 0) toast.info("No online matches — try a shorter name");
    } catch { toast.error("Search failed"); } finally { setSearching(false); }
  };

  useEffect(() => {
    if (item.sourceId) check(item.sourceId); else search(item.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async () => {
    setApplying(true);
    try {
      const updated = await updateLibraryCourse(item.id, {
        name: item.name, subtitle: item.subtitle, teeBoxes: result.proposedTeeBoxes, sourceId: result.sourceId,
      });
      toast.success(`Updated "${item.name}" (${result.changes.length} tee box change${result.changes.length === 1 ? "" : "s"})`);
      setResult({ ...result, upToDate: true, changes: [] });
      onApplied(updated);
    } catch { toast.error("Update failed"); } finally { setApplying(false); }
  };

  return (
    <div className="mt-3 border border-[color:var(--border-brass)] rounded-lg p-3 bg-[rgba(27,51,34,0.04)]" data-testid={`update-panel-${item.id}`}>
      {checking && <p className="helptext text-[color:var(--text-dark)]/70 inline-flex items-center gap-2"><RefreshCw size={12} className="animate-spin" /> Checking online course data…</p>}

      {!checking && !result && !item.sourceId && (
        <div>
          <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-1 inline-flex items-center gap-1"><Link2 size={12} /> Link to an online course</div>
          <div className="flex gap-2 mb-2">
            <input data-testid={`update-link-search-input-${item.id}`} className="tga-input-light flex-1 !py-1" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(query); }} />
            <button data-testid={`update-link-search-btn-${item.id}`} className="brass-btn rounded-md px-3 text-xs inline-flex items-center gap-1" disabled={searching} onClick={() => search(query)}><Search size={12} /> {searching ? "…" : "Search"}</button>
          </div>
          {matches.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-[rgba(27,51,34,0.15)] rounded-md p-2 mb-1">
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{c.club_name || c.course_name}</div>
                <div className="text-[11px] opacity-70 truncate">{c.course_name && c.club_name !== c.course_name ? `${c.course_name} · ` : ""}{[c.location?.city, c.location?.state, c.location?.country].filter(Boolean).join(", ")}</div>
              </div>
              <button data-testid={`update-link-pick-${c.id}`} className="brass-btn rounded-md px-3 py-1 text-xs" onClick={() => check(c.id)}>Link & check</button>
            </div>
          ))}
        </div>
      )}

      {!checking && result && result.upToDate && (
        <div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm inline-flex items-center gap-1 text-[color:var(--text-dark)]" data-testid={`update-uptodate-${item.id}`}><Check size={14} /> Up to date with {result.remoteName || "online data"}</div>
            <button className="ghost-btn rounded-md px-3 py-1 text-xs" onClick={onClose}>Close</button>
          </div>
          <PushToTrips item={item} onPushed={onPushed} />
        </div>
      )}

      {!checking && result && !result.upToDate && (
        <div>
          <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-2" data-testid={`update-changes-${item.id}`}>
            {result.changes.length} change{result.changes.length === 1 ? "" : "s"} found · {result.remoteName}
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
            {result.changes.map((ch) => (
              <div key={ch.tee} className="text-sm">
                <span className="font-display">{ch.tee}</span>
                <span className="text-[11px] uppercase ml-2 px-1.5 py-0.5 rounded bg-[color:var(--brass-dark)]/15">{ch.type}</span>
                <div className="text-xs opacity-80 font-mono-metrics mt-0.5">{ch.details.join(" · ")}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid={`update-apply-${item.id}`} disabled={applying} className="brass-btn rounded-md px-4 py-1.5 text-sm inline-flex items-center gap-1" onClick={apply}><RefreshCw size={13} /> {applying ? "Applying…" : "Apply update"}</button>
            <button data-testid={`update-dismiss-${item.id}`} className="ghost-btn rounded-md px-4 py-1.5 text-sm" onClick={onClose}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}
